import {
    api,
    apiErrorMessage,
    listSpec,
    oneSpec,
    useAuthStore,
    useIsPlatformHost,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    ConfirmDialog,
    DateField,
    DateTime,
    DetailRow,
    Field,
    FieldGrid,
    Input,
    PageHeader,
    ReasonDialog,
    StatusBadge,
    Textarea,
    UnsavedGuard,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import type { Flag, FlagChange, FlagUpdateRequest } from '@/api/types';
import { DetailBody } from '@/components/detail';
import { P } from '@/lib/permissions';
import { useList, useOne, useWrite } from '@/lib/queries';
import { useMe, who } from '@/lib/staff';
import { ADMIN_STATUSES } from '@/lib/statuses';
import { FlagScope, FlagState } from '@/pages/flags/index';

/** What each seeded kill switch stops, so the person flipping it knows the blast radius. */
const KILL_SWITCH_EFFECT: Record<string, string> = {
    'payments.nmi.enabled':
        'Every new charge and refund is refused; renewals wait.',
    'billing.renewals.enabled':
        'No renewal order is created; subscriptions keep their period.',
    'delivery.webhooks.enabled':
        'Merchant webhooks queue up and are not delivered.',
    'ledger.payouts.enabled': 'No payout run starts; balances keep accruing.',
    'risk.pre_auth.enforce':
        'Risk rules observe only; no checkout is declined by them.',
};

/** The reads of `/flags/:key` (BM-46 §2.0). */
export const flagReads = (key: string | undefined) => ({
    flag: oneSpec(`/staff/flags/${key ?? ''}`),
    changes: listSpec(`/staff/flags/${key ?? ''}/changes`, { limit: 50 }),
});

export function FlagPage() {
    const { key = '' } = useParams();
    const me = useMe();
    const can = useAuthStore((s) => s.can);
    // BM-105: on a tenant board a platform flag is read-only unless the platform opened it (then an edit writes the tenant's override)
    const platformHost = useIsPlatformHost();
    const query = useOne<Flag>(flagReads(key).flag.path);
    const changes = useList<FlagChange>(
        flagReads(key).changes.path,
        flagReads(key).changes.params,
    );
    const [edit, setEdit] = useState<{
        description: string;
        owner: string;
        ticket: string;
        expires_at: string;
        rules: string;
    } | null>(null);
    const [flip, setFlip] = useState<boolean | null>(null);
    const [archive, setArchive] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const update = useWrite(
        async (body: FlagUpdateRequest) =>
            (await api().patch<Flag>(`/staff/flags/${key}`, body)).data,
        ['/staff/flags'],
    );
    const doArchive = useWrite(
        async (version: number) =>
            (await api().post<Flag>(`/staff/flags/${key}/archive`, { version }))
                .data,
        ['/staff/flags'],
    );
    const onError = (e: unknown) => setError(apiErrorMessage(e));
    return (
        <div>
            {/* BM-46 §2.9: leaving with unsaved changes asks first */}
            <UnsavedGuard dirty={edit !== null} />
            <PageHeader
                crumbs={[{ label: 'Flags', href: '/flags' }]}
                title={key}
                meta={<AsOfBadge asOf={query.freshness.asOf} />}
            />
            <DetailBody query={query} title="Flag">
                {(f) => {
                    const kill = f.kind === 'kill_switch';
                    const platformFlag = f.tenant_id === null;
                    // a tenant board: the platform's flag is read-only unless opened; a kill switch is the platform host's alone
                    const readOnly =
                        !platformHost &&
                        platformFlag &&
                        (kill || !f.tenant_overridable);
                    return (
                        <div className="space-y-4">
                            {error ? (
                                <p
                                    role="alert"
                                    className="text-destructive text-sm"
                                >
                                    {error}
                                </p>
                            ) : null}
                            <Card>
                                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                                    <CardTitle className="text-base">
                                        <StatusBadge
                                            status={f.kind}
                                            domain={ADMIN_STATUSES.flagKind}
                                        />{' '}
                                        <FlagScope f={f} /> <FlagState f={f} />
                                    </CardTitle>
                                    {readOnly ? (
                                        <p
                                            className="text-muted-foreground text-sm"
                                            data-testid="flag-read-only"
                                        >
                                            {kill
                                                ? 'A kill switch is flipped on the platform host.'
                                                : "The platform's flag; it is not open to overrides on this board."}
                                        </p>
                                    ) : null}
                                    {!f.archived_at && !readOnly ? (
                                        <div
                                            className="flex flex-wrap gap-2"
                                            data-testid="flag-actions"
                                        >
                                            {platformHost && !kill ? (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() =>
                                                        update.mutate(
                                                            {
                                                                version:
                                                                    f.version,
                                                                tenant_overridable:
                                                                    !f.tenant_overridable,
                                                            },
                                                            { onError },
                                                        )
                                                    }
                                                >
                                                    {f.tenant_overridable
                                                        ? 'Close to tenants'
                                                        : 'Open to tenants'}
                                                </Button>
                                            ) : null}
                                            {kill && can(P.flagsKillSwitch) ? (
                                                <Button
                                                    size="sm"
                                                    // BM-66 (design KillSwitch): an emergency stop is danger both ways, never the accent
                                                    variant="destructive"
                                                    onClick={() =>
                                                        setFlip(!f.default_on)
                                                    }
                                                >
                                                    {f.default_on
                                                        ? 'Switch off'
                                                        : 'Switch on'}
                                                </Button>
                                            ) : null}
                                            {!kill ? (
                                                <>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() =>
                                                            update.mutate(
                                                                {
                                                                    version:
                                                                        f.version,
                                                                    default_on:
                                                                        !f.default_on,
                                                                },
                                                                { onError },
                                                            )
                                                        }
                                                    >
                                                        {f.default_on
                                                            ? 'Default off'
                                                            : 'Default on'}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() =>
                                                            setEdit({
                                                                description:
                                                                    f.description,
                                                                owner:
                                                                    f.owner ??
                                                                    '',
                                                                ticket:
                                                                    f.ticket ??
                                                                    '',
                                                                expires_at:
                                                                    f.expires_at ??
                                                                    '',
                                                                rules: JSON.stringify(
                                                                    f.rules,
                                                                    null,
                                                                    2,
                                                                ),
                                                            })
                                                        }
                                                    >
                                                        Edit
                                                    </Button>
                                                    {platformHost ||
                                                    !platformFlag ? (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() =>
                                                                setArchive(true)
                                                            }
                                                        >
                                                            Archive
                                                        </Button>
                                                    ) : null}
                                                </>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </CardHeader>
                                <CardContent>
                                    {kill ? (
                                        <p
                                            className="mb-3 text-sm"
                                            data-testid="kill-switch-effect"
                                        >
                                            <span className="font-medium">
                                                Off means:
                                            </span>{' '}
                                            {KILL_SWITCH_EFFECT[f.key] ??
                                                'the owning service stops the feature.'}
                                        </p>
                                    ) : null}
                                    <FieldGrid>
                                        <DetailRow
                                            label="What it switches"
                                            value={f.description}
                                            layout="stacked"
                                        />
                                        <DetailRow
                                            label="Owner"
                                            value={f.owner ?? '—'}
                                            layout="stacked"
                                        />
                                        <DetailRow
                                            label="Ticket"
                                            value={f.ticket ?? '—'}
                                            layout="stacked"
                                        />
                                        <DetailRow
                                            label="Expires"
                                            layout="stacked"
                                        >
                                            {/* a calendar day: that day, never moved by a zone */}
                                            <DateTime
                                                value={f.expires_at}
                                                format="day"
                                                empty="—"
                                            />
                                        </DetailRow>
                                        <DetailRow
                                            label="Variants"
                                            value={
                                                f.variants?.join(', ') ?? '—'
                                            }
                                            layout="stacked"
                                        />
                                        <DetailRow
                                            label="Version"
                                            value={String(f.version)}
                                            layout="stacked"
                                        />
                                        <DetailRow
                                            label="Updated"
                                            layout="stacked"
                                        >
                                            <DateTime
                                                zone="inline"
                                                value={f.updated_at}
                                                empty="—"
                                            />{' '}
                                            by {who(f.updated_by, me)}
                                        </DetailRow>
                                    </FieldGrid>
                                    {f.rules.length > 0 ? (
                                        <pre className="bg-muted mt-4 overflow-x-auto rounded p-2 text-xs">
                                            {JSON.stringify(f.rules, null, 2)}
                                        </pre>
                                    ) : null}
                                    {edit ? (
                                        <form
                                            method="post"
                                            className="mt-4 space-y-3 border-t pt-4"
                                            data-testid="flag-edit"
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                let rules: Record<
                                                    string,
                                                    unknown
                                                >[];
                                                try {
                                                    rules = JSON.parse(
                                                        edit.rules || '[]',
                                                    );
                                                } catch {
                                                    setError(
                                                        'Rules must be a JSON list.',
                                                    );
                                                    return;
                                                }
                                                setError(null);
                                                update.mutate(
                                                    {
                                                        version: f.version,
                                                        description:
                                                            edit.description,
                                                        owner: edit.owner,
                                                        ticket: edit.ticket,
                                                        expires_at:
                                                            edit.expires_at,
                                                        rules,
                                                    },
                                                    {
                                                        onSuccess: () =>
                                                            setEdit(null),
                                                        onError,
                                                    },
                                                );
                                            }}
                                        >
                                            <Field
                                                id="description"
                                                label="What it switches"
                                            >
                                                <Input
                                                    id="description"
                                                    value={edit.description}
                                                    onChange={(e) =>
                                                        setEdit({
                                                            ...edit,
                                                            description:
                                                                e.target.value,
                                                        })
                                                    }
                                                />
                                            </Field>
                                            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                                <Field id="owner" label="Owner">
                                                    <Input
                                                        id="owner"
                                                        value={edit.owner}
                                                        onChange={(e) =>
                                                            setEdit({
                                                                ...edit,
                                                                owner: e.target
                                                                    .value,
                                                            })
                                                        }
                                                    />
                                                </Field>
                                                <Field
                                                    id="ticket"
                                                    label="Ticket"
                                                >
                                                    <Input
                                                        id="ticket"
                                                        value={edit.ticket}
                                                        onChange={(e) =>
                                                            setEdit({
                                                                ...edit,
                                                                ticket: e.target
                                                                    .value,
                                                            })
                                                        }
                                                    />
                                                </Field>
                                                <Field
                                                    id="expires_at"
                                                    label="Expires"
                                                >
                                                    <DateField
                                                        id="expires_at"
                                                        value={
                                                            edit.expires_at ||
                                                            null
                                                        }
                                                        onChange={(day) =>
                                                            setEdit({
                                                                ...edit,
                                                                expires_at:
                                                                    day ?? '',
                                                            })
                                                        }
                                                    />
                                                </Field>
                                            </div>
                                            <Field
                                                id="rules"
                                                label="Rules (JSON)"
                                                hint="One rule per entry, evaluated in order: env, tenants, merchants, stores, staff or percent_bps, with on and variant. Put a store rule before its merchant's."
                                            >
                                                <Textarea
                                                    id="rules"
                                                    rows={5}
                                                    className="font-mono text-xs"
                                                    value={edit.rules}
                                                    onChange={(e) =>
                                                        setEdit({
                                                            ...edit,
                                                            rules: e.target
                                                                .value,
                                                        })
                                                    }
                                                />
                                            </Field>
                                            <div className="flex gap-2">
                                                <Button
                                                    type="submit"
                                                    size="sm"
                                                    disabled={update.isPending}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() =>
                                                        setEdit(null)
                                                    }
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </form>
                                    ) : null}
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-sm font-medium">
                                        Change log
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul
                                        className="divide-y text-sm"
                                        data-testid="flag-changes"
                                    >
                                        {(changes.data?.data.items ?? []).map(
                                            (c) => (
                                                <li
                                                    key={c.version}
                                                    className="py-2"
                                                >
                                                    <p>
                                                        <span className="font-medium">
                                                            v{c.version}
                                                        </span>{' '}
                                                        ·{' '}
                                                        {who(c.changed_by, me)}{' '}
                                                        ·{' '}
                                                        <DateTime
                                                            value={c.created_at}
                                                            empty="—"
                                                        />
                                                    </p>
                                                    {c.reason ? (
                                                        <p className="text-muted-foreground">
                                                            {c.reason}
                                                        </p>
                                                    ) : null}
                                                    <details className="text-xs">
                                                        <summary className="cursor-pointer">
                                                            before / after
                                                        </summary>
                                                        <pre className="bg-muted mt-1 overflow-x-auto rounded p-2">
                                                            {JSON.stringify(
                                                                {
                                                                    before: c.before,
                                                                    after: c.after,
                                                                },
                                                                null,
                                                                2,
                                                            )}
                                                        </pre>
                                                    </details>
                                                </li>
                                            ),
                                        )}
                                        {changes.data &&
                                        changes.data.data.items.length === 0 ? (
                                            <li className="text-muted-foreground py-2">
                                                No change yet.
                                            </li>
                                        ) : null}
                                    </ul>
                                </CardContent>
                            </Card>
                            {flip !== null ? (
                                <ReasonDialog
                                    open
                                    onOpenChange={(o) =>
                                        o ? null : setFlip(null)
                                    }
                                    title={
                                        flip
                                            ? `Switch ${f.key} on`
                                            : `Switch ${f.key} off`
                                    }
                                    description={
                                        flip
                                            ? 'The owning service resumes on the next flag.changed.'
                                            : `${KILL_SWITCH_EFFECT[f.key] ?? 'The feature stops.'} A case opens in the kill-switches queue.`
                                    }
                                    confirmLabel={
                                        flip ? 'Switch on' : 'Switch off'
                                    }
                                    destructive
                                    busy={update.isPending}
                                    error={update.error}
                                    onConfirm={(reason) =>
                                        update.mutate(
                                            {
                                                version: f.version,
                                                default_on: flip,
                                                reason,
                                            },
                                            { onSuccess: () => setFlip(null) },
                                        )
                                    }
                                />
                            ) : null}
                            <ConfirmDialog
                                open={archive}
                                onOpenChange={setArchive}
                                title="Archive this flag"
                                description="A referencing service's CI fails until the code stops reading it."
                                confirmLabel="Archive"
                                destructive
                                busy={doArchive.isPending}
                                onConfirm={() =>
                                    doArchive.mutate(f.version, {
                                        onSuccess: () => setArchive(false),
                                        onError,
                                    })
                                }
                            />
                        </div>
                    );
                }}
            </DetailBody>
        </div>
    );
}
